import { useQueryClient } from '@tanstack/react-query';
import { memo, type ReactNode, useCallback } from 'react';

import { fetchCompanies, fetchCompany, queryKeysCompany } from '~/data/company';
import { useGetUserUtils } from '~/data/user';

import { companyCityOptionLabel } from './MultiSelectCompanies';
import { type Option, SelectServerDriven } from './SelectServerDriven';
import { createGetItemData, createLoadOptions } from './utils';

// Define a Company type based on the expected structure
type Company = {
  id: string;
  name: string;
};

type SelectCompanyProps = {
  readonly formatOptionLabel?: (option: Option<Company>) => ReactNode;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly placeholder?: string;
  readonly onChange: (value: string) => void;
  readonly value?: string;
} & ComponentStyling;

const getOptionLabel = ({ name }: Company) => name;

export const SelectCompany = memo(
  ({
    className,
    formatOptionLabel = companyCityOptionLabel,
    isClearable = true,
    isDisabled = false,
    onChange,
    placeholder = 'Firma auswählen',
    style,
    value,
  }: SelectCompanyProps) => {
    const queryClient = useQueryClient();

    const { UserUtils } = useGetUserUtils();

    const loadOptions = createLoadOptions<Company>({
      createQueryKey: ({ page, ...queryParams }) =>
        queryKeysCompany.getAll(queryParams),
      async fetchItems({ page, ...queryParams }) {
        const { data, hasNextPage } = await fetchCompanies(queryParams);

        return {
          data,
          hasNextPage,
        };
      },
      queryClient,
    });

    const getItemData = createGetItemData<Company>({
      createQueryKey: (companyId) => queryKeysCompany.get(companyId),
      fetchItem: fetchCompany,
      queryClient,
      queryKeyBase: [queryKeysCompany.base()],
    });

    const handleChange = useCallback(
      (newValue: string) => {
        onChange(newValue);
      },
      [onChange],
    );

    return (
      <SelectServerDriven<Company>
        className={className}
        formatOptionLabel={formatOptionLabel}
        getItemData={getItemData}
        getOptionLabel={getOptionLabel}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isItemSelectionAllowed={UserUtils.isCompanyReadAllowedUser}
        isMultiSelect={false}
        loadOptions={loadOptions}
        placeholder={placeholder}
        style={style}
        value={value}
        onChange={handleChange}
      />
    );
  },
);
